import React, { useState, useEffect } from "react";
import SEO from "../component/SEO";
import { BsCloudUpload, BsTrash } from "react-icons/bs";
import { ImagetoBase64 } from "../utility/ImagetoBase64";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";

const OfferManagement = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newOffer, setNewOffer] = useState({
    title: "",
    image: "",
    link: "",
    priority: 999,
  });

  const token = useSelector((state) => state.user.token);

  // Fetch existing offers
  const fetchOffers = async (pageNum, isInitial = false) => {
    if (isInitial) setLoading(true);
    else setLoadingMore(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/offers?page=${pageNum}&limit=10`);
      const data = await res.json();
      if (!res.ok) toast.error(data.message || "Failed to fetch offers");
      else {
        const newOffers = data.offers || [];
        setOffers((prev) => (isInitial ? newOffers : [...prev, ...newOffers]));
        setHasMore(data.hasMore);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch offers");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchOffers(1, true);
  }, []);

  const handleScroll = () => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100 &&
      hasMore &&
      !loadingMore &&
      !loading
    ) {
      setPage((prev) => {
        const nextPage = prev + 1;
        fetchOffers(nextPage);
        return nextPage;
      });
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingMore, loading]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewOffer((prev) => ({ ...prev, [name]: value }));
  };

  // Upload image
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const base64 = await ImagetoBase64(file);
    setNewOffer((prev) => ({ ...prev, image: base64 }));
  };

  // Add new offer
  const addOffer = async () => {
    const { title, image, link, priority } = newOffer;
    if (!image) return toast.error("Image is required");
    if (!token) return toast.error("You are not logged in");

    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/admin/offer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title, image, link, priority: Number(priority) }),
      });
      const data = await res.json();
      toast(data.message || "Offer added");
      if (res.ok) {
        setNewOffer({ title: "", image: "", link: "", priority: 999 });
        setPage(1);
        fetchOffers(1, true);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to add offer");
    }
  };

  // Delete an offer
  const deleteOffer = async (id) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/admin/offer/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      toast(data.message || "Offer deleted");
      setPage(1);
      fetchOffers(1, true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete offer");
    }
  };

  return (
    <div className="p-5 max-w-3xl mx-auto">
      <SEO title="Offer Management - K Bakery" description="Manage offers and promotions." />
      <h1 className="text-2xl font-bold mb-4">Offer Management</h1>

      {/* Add New Offer */}
      <div className="bg-white p-4 rounded shadow mb-6">
        <h3 className="font-semibold mb-2">Add New Offer</h3>

        <input
          type="text"
          name="title"
          placeholder="Offer title"
          className="w-full p-2 mb-2 border rounded"
          value={newOffer.title}
          onChange={handleChange}
        />
        <input
          type="text"
          name="link"
          placeholder="Optional link"
          className="w-full p-2 mb-2 border rounded"
          value={newOffer.link}
          onChange={handleChange}
        />
        <input
          type="number"
          name="priority"
          placeholder="Priority"
          className="w-full p-2 mb-2 border rounded"
          value={newOffer.priority}
          onChange={handleChange}
        />

        <label className="block mb-2 cursor-pointer">
          <div className="h-40 w-full bg-purple-100 rounded flex items-center justify-center">
            {newOffer.image ? (
              <img src={newOffer.image} className="h-full object-contain" alt="offer" />
            ) : (
              <span className="text-5xl text-gray-500">
                <BsCloudUpload />
              </span>
            )}
          </div>
          <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
        </label>

        <button
          onClick={addOffer}
          className="mt-2 w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded"
        >
          Add Offer
        </button>
      </div>

      {/* Existing Offers */}
      <h3 className="text-xl font-semibold mb-2">Existing Offers</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {offers.map((offer) => (
          <div key={offer._id} className="bg-white p-3 rounded shadow relative">
            <img src={offer.image} alt={offer.title} className="w-full h-40 object-cover rounded" />
            <div className="mt-2">
              <p className="font-semibold">{offer.title || "No Title"}</p>
              {offer.link && <p className="text-sm text-blue-500">{offer.link}</p>}
            </div>
            <button
              onClick={() => deleteOffer(offer._id)}
              className="absolute top-2 right-2 text-red-500 text-xl"
            >
              <BsTrash />
            </button>
          </div>
        ))}
      </div>
      {loadingMore && (
        <p className="text-center mt-6 text-gray-500 animate-pulse">
          Loading more offers...
        </p>
      )}
    </div>
  );
};

export default OfferManagement;
